/**
 * Tự động hoá v2 (demo FE) — trang chính 3 tab: Luồng · Nguồn dữ liệu · Ánh xạ.
 * Nhật ký chạy nằm trong từng luồng (nút Nhật ký ở trình dựng), không gom chung.
 * Dữ liệu ở localStorage (hooks/use-automation-v2). Module cũ /automation giữ nguyên.
 */
import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Plus, Copy, Trash2, Pencil, Workflow, Plug, ArrowLeftRight, History, Download, Upload, RotateCcw,
  CheckCircle2, AlertCircle, ExternalLink, KeyRound, Eye, EyeOff,
} from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { EmptyState } from '@/components/shared/feedback'
import { cn } from '@/lib/utils'
import { storeV2, useStoreV2 } from '@/hooks/use-automation-v2'
import { TabNguon } from './nguon-du-lieu'
import {
  SOURCE_LABELS, SOURCE_EVENTS, TRANSFORM_LABELS, genId,
  type Connector, type Mapping, type SourceType, type Transform,
} from '@/lib/automation-v2/types'
import { INTERNAL_OBJECTS, INTERNAL_EVENT_LABELS, type InternalEvent, type InternalObject } from '@/lib/automation-v2/kich-hoat'
import { LICH_LABELS } from './khoi-canvas'
import { KIOTVIET_BASE_URL, KIOTVIET_TOKEN_URL, KIOTVIET_QUOTA, KIOTVIET_SAMPLES, KIOTVIET_ORDER_SCHEMA, CRM_SCHEMA } from '@/lib/automation-v2/kiotviet'

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('vi-VN') : '—')

export function AutomationV2Page() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') ?? 'flows'
  return (
    <div className="space-y-4">
      <PageHeader
        title="Tự động hoá v2"
        description="Dựng luồng kiểu n8n: Kích hoạt → Ánh xạ → Điều kiện → Chờ → Hành động. Luồng đang BẬT chạy thật trên máy chủ; dữ liệu KiotViet và webhook về qua tab Nguồn dữ liệu."
      />
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="success">Chạy thật trên máy chủ</Badge>
        <Link to="/automation" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink className="h-3 w-3" /> Mở bản cũ (tham chiếu)</Link>
        <span className="ml-auto flex gap-1"><XuatNhap /></span>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList>
          <TabsTrigger value="flows"><Workflow className="mr-1 h-4 w-4" /> Luồng tự động</TabsTrigger>
          <TabsTrigger value="sources"><Plug className="mr-1 h-4 w-4" /> Nguồn dữ liệu</TabsTrigger>
          <TabsTrigger value="mappings"><ArrowLeftRight className="mr-1 h-4 w-4" /> Ánh xạ</TabsTrigger>
        </TabsList>
        <TabsContent value="flows"><TabLuong /></TabsContent>
        <TabsContent value="sources"><TabNguon /></TabsContent>
        <TabsContent value="mappings"><TabAnhXa /></TabsContent>
      </Tabs>
    </div>
  )
}

// ── Xuất / nhập / đặt lại ────────────────────────────────────────────
function XuatNhap() {
  const fileRef = useRef<HTMLInputElement>(null)
  const xuat = () => {
    const { workflows, mappings } = storeV2.get()
    const blob = new Blob([JSON.stringify({ workflows, mappings }, null, 2)], { type: 'application/json' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `automation-v2-${Date.now()}.json`; a.click()
  }
  const nhap = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = ''; if (!f) return
    let json: Parameters<typeof storeV2.importJson>[0]
    try { json = JSON.parse(await f.text()) } catch { toast.error('Tệp JSON không hợp lệ'); return }
    await storeV2.importJson(json); toast.success('Đã nhập luồng từ tệp (luồng nhập vào ở trạng thái tắt)')
  }
  return (
    <>
      <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={nhap} />
      <Button size="sm" variant="ghost" onClick={xuat}><Download className="h-3.5 w-3.5" /> Xuất JSON</Button>
      <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()}><Upload className="h-3.5 w-3.5" /> Nhập JSON</Button>
      <Button size="sm" variant="ghost" onClick={() => { void storeV2.reset() }} title="Nạp các luồng mẫu còn thiếu (không đụng luồng đã có)"><RotateCcw className="h-3.5 w-3.5" /> Nạp luồng mẫu</Button>
    </>
  )
}

// ── Tab Luồng ────────────────────────────────────────────────────────
function TabLuong() {
  const { workflows, connectors } = useStoreV2()
  const nav = useNavigate()
  const [tenMoi, setTenMoi] = useState('')
  const [moTao, setMoTao] = useState(false)
  /** Mô tả ngắn khối Kích hoạt của luồng, theo cả 4 chế độ. */
  const tenNguon = (wf: (typeof workflows)[number]) => {
    const src = wf.nodes.find((n) => n.kind === 'source')
    const cfg = src?.config ?? {}
    const mode = String(cfg.mode ?? 'external')
    if (mode === 'internal') {
      const o = INTERNAL_OBJECTS[cfg.object as InternalObject]
      const ev = INTERNAL_EVENT_LABELS[cfg.internalEvent as InternalEvent]
      const truong = cfg.field ? ` · ${String(cfg.field).split('.').pop()}${cfg.toValue ? ` → ${cfg.toValue}` : ''}` : ''
      return `${o?.ten ?? 'Bản ghi'} · ${ev?.ten ?? 'sự kiện'}${truong}`
    }
    if (mode === 'schedule') return `Theo lịch · ${LICH_LABELS[String(cfg.cron)] ?? String(cfg.cron ?? 'chưa đặt')}`
    if (mode === 'manual') return `Thủ công · nút "${cfg.buttonLabel || 'Chạy luồng'}"`
    const c = connectors.find((x) => x.id === cfg.connectorId)
    const ev = SOURCE_EVENTS[c?.type ?? 'chatmql']?.find((e) => e.key === cfg.event)
    return c ? `${SOURCE_LABELS[c.type]} · ${ev?.label ?? cfg.event ?? '—'}` : 'Chưa chọn nguồn'
  }
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setMoTao(true)}><Plus className="h-4 w-4" /> Tạo luồng mới</Button>
      </div>
      {workflows.length === 0 && <EmptyState icon={Workflow} title="Chưa có luồng nào" description="Bấm Tạo luồng mới hoặc Đặt lại mẫu." />}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {workflows.map((wf) => (
          <Card key={wf.id} className={cn('flex flex-col', !wf.enabled && 'opacity-80')}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base leading-snug">{wf.name}</CardTitle>
                <Switch checked={wf.enabled} onCheckedChange={(v) => storeV2.toggleWorkflow(wf.id, v)} title={wf.enabled ? 'Đang bật' : 'Đang tắt'} />
              </div>
              <CardDescription className="line-clamp-2">{wf.description || tenNguon(wf)}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col gap-2 text-xs text-muted-foreground">
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline">{tenNguon(wf)}</Badge>
                <Badge variant="secondary">{wf.nodes.length} khối</Badge>
                {wf.lastRunStatus && (
                  <Badge variant={wf.lastRunStatus === 'ok' ? 'success' : 'destructive'}>
                    {wf.lastRunStatus === 'ok' ? <CheckCircle2 className="mr-1 h-3 w-3" /> : <AlertCircle className="mr-1 h-3 w-3" />}
                    chạy thử {fmt(wf.lastRunAt)}
                  </Badge>
                )}
              </div>
              <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
                <Button size="sm" onClick={() => nav(`/automation-v2/flow/${wf.id}`)}><Pencil className="h-3.5 w-3.5" /> Chỉnh sửa</Button>
                <Button size="sm" variant="outline" onClick={() => nav(`/automation-v2/flow/${wf.id}?nhatky=1`)}><History className="h-3.5 w-3.5" /> Nhật ký</Button>
                <Button size="sm" variant="outline" onClick={() => storeV2.duplicateWorkflow(wf.id)}><Copy className="h-3.5 w-3.5" /> Nhân bản</Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => { if (confirm(`Xoá luồng "${wf.name}"?`)) storeV2.deleteWorkflow(wf.id) }}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={moTao} onOpenChange={setMoTao}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tạo luồng mới</DialogTitle><DialogDescription>Luồng mới có sẵn khối Nguồn dữ liệu; vào trình dựng để thêm Ánh xạ, Điều kiện, Hành động.</DialogDescription></DialogHeader>
          <Input autoFocus placeholder="Tên luồng, vd: Đơn Shopee giao thành công → xin đánh giá" value={tenMoi} onChange={(e) => setTenMoi(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoTao(false)}>Huỷ</Button>
            <Button disabled={!tenMoi.trim()} onClick={async () => { const wf = await storeV2.newWorkflow(tenMoi.trim()); setMoTao(false); setTenMoi(''); nav(`/automation-v2/flow/${wf.id}`) }}>Tạo & mở</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Tab Nguồn dữ liệu ────────────────────────────────────────────────

// ── Tab Ánh xạ ───────────────────────────────────────────────────────
function TabAnhXa() {
  const { mappings } = useStoreV2()
  const [chon, setChon] = useState(mappings[0]?.id ?? '')
  const m = mappings.find((x) => x.id === chon) ?? mappings[0]
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={m?.id ?? ''} onValueChange={setChon}>
          <SelectTrigger className="h-9 w-80 text-sm"><SelectValue placeholder="Chọn bảng ánh xạ" /></SelectTrigger>
          <SelectContent>{mappings.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => { const nm: Mapping = { id: genId('map'), source: 'webhook', name: 'Ánh xạ mới', rules: [{ from: '', to: '', transform: 'none' }] }; storeV2.saveMapping(nm); setChon(nm.id) }}><Plus className="h-3.5 w-3.5" /> Bảng ánh xạ mới</Button>
      </div>
      {m && <BangAnhXa key={m.id} m={m} />}
    </div>
  )
}

function BangAnhXa({ m }: { m: Mapping }) {
  const [rules, setRules] = useState(m.rules)
  const [ten, setTen] = useState(m.name)
  const truongNguon = useMemo(() => m.source === 'kiotviet' ? KIOTVIET_ORDER_SCHEMA.flatMap((f) => (f.children ? [f, ...f.children] : [f])) : [], [m.source])
  const truongDich = useMemo(() => CRM_SCHEMA.flatMap((f) => (f.children ? [f, ...f.children] : [f])), [])
  const sua = (i: number, k: keyof typeof rules[number], v: string) => setRules((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)))
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input value={ten} onChange={(e) => setTen(e.target.value)} className="h-9 max-w-md font-semibold" />
          <Badge variant="outline">{SOURCE_LABELS[m.source]}</Badge>
        </div>
        <CardDescription>Trái: đường dẫn trong payload nguồn (mảng dùng <code>[]</code>, vd <code>OrderDetails[].ProductName</code>). Phải: trường nội bộ CRM. SĐT nên chuẩn hoá để làm khoá ghép với khách Zalo.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-[1fr_1fr_11rem_2rem] gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"><span>Từ (nguồn)</span><span>Sang (CRM)</span><span>Biến đổi</span><span /></div>
        {rules.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_11rem_2rem] items-center gap-2">
            <Input list={`nguon-${m.id}`} value={r.from} onChange={(e) => sua(i, 'from', e.target.value)} className="h-8 font-mono text-xs" placeholder="Customer.ContactNumber" />
            <Input list={`dich-${m.id}`} value={r.to} onChange={(e) => sua(i, 'to', e.target.value)} className="h-8 font-mono text-xs" placeholder="customer.phone" />
            <Select value={r.transform} onValueChange={(v) => sua(i, 'transform', v as Transform)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{(Object.keys(TRANSFORM_LABELS) as Transform[]).map((t) => <SelectItem key={t} value={t}>{TRANSFORM_LABELS[t]}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        <datalist id={`nguon-${m.id}`}>{truongNguon.map((f) => <option key={f.path} value={f.path}>{f.label}</option>)}</datalist>
        <datalist id={`dich-${m.id}`}>{truongDich.map((f) => <option key={f.path} value={f.path}>{f.label}</option>)}</datalist>
        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={() => setRules((rs) => [...rs, { from: '', to: '', transform: 'none' }])}><Plus className="h-3.5 w-3.5" /> Thêm dòng</Button>
          <Button size="sm" onClick={() => { storeV2.saveMapping({ ...m, name: ten, rules: rules.filter((r) => r.from && r.to) }); toast.success('Đã lưu ánh xạ') }}>Lưu ánh xạ</Button>
        </div>
      </CardContent>
    </Card>
  )
}

